import React, { useEffect, useRef, useState } from 'react';
import { ITEM_SHEET_GRID } from '../../data/guide/itemSpriteSheets';
import { BOUNDS_FIT_FRACTION, BOUNDS_REFERENCE_CELL } from '../../data/guide/itemSpriteBounds';
import type { SpriteBounds } from '../../data/guide/itemSpriteBounds';
import { watchSheet } from '../../utils/itemSprites';
import type { SheetStatus, SpriteCell } from '../../utils/itemSprites';

/** Margen de seguridad (px de la celda de referencia) alrededor del rectángulo visible, sin salir de la celda. */
const PAD = 2;
/** Separación mínima del borde de la celda, para que el redondeo nunca muestre un píxel de la celda vecina. */
const EDGE = 0.5;
const SHEET_REF = ITEM_SHEET_GRID * BOUNDS_REFERENCE_CELL;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Ventana de recorte en px de referencia (celda 256) y su escala dentro del cuadro. */
export function spriteWindow(bounds: SpriteBounds) {
  const C = BOUNDS_REFERENCE_CELL;
  // Rectángulo del encuadre limitado a su celda (un encuadre mal escrito nunca invade otra celda).
  const bx = clamp(bounds[0], 0, C);
  const by = clamp(bounds[1], 0, C);
  const bw = clamp(bounds[2], 1, C - bx);
  const bh = clamp(bounds[3], 1, C - by);
  const x0 = clamp(bx - PAD, EDGE, C - EDGE);
  const y0 = clamp(by - PAD, EDGE, C - EDGE);
  const w = Math.max(1, clamp(bx + bw + PAD, EDGE, C - EDGE) - x0);
  const h = Math.max(1, clamp(by + bh + PAD, EDGE, C - EDGE) - y0);
  // Escala uniforme: lado mayor del ítem ≈ 78 % del cuadro, pero la ventana entera siempre cabe (nunca se corta).
  const k = Math.min(BOUNDS_FIT_FRACTION / Math.max(bw, bh), 1 / Math.max(w, h));
  return {
    x0,
    y0,
    w,
    h,
    k
  };
}

/**
 * Muestra un ítem de una lámina 8 x 8 (componente compartido por fichas, listados, buscador, materiales y vistas previas).
 * - Con encuadre: aísla SOLO el rectángulo visible del ítem en una ventana con overflow oculto, la centra y la amplía.
 *   Coordenadas en porcentajes sobre la celda de referencia de 256: igual con láminas de 2048, 1920 o 1024 px.
 * - Sin encuadre: la celda completa.
 * - Mientras la lámina se descarga y decodifica: fondo tenue (no es un cuadro vacío). Si falla: onError.
 * Con lazy, la lámina solo se pide cuando el elemento entra en pantalla.
 */
export function SpriteBox({
  cell,
  url,
  bounds,
  lazy = true,
  className = '',
  onError
}: {
  cell: SpriteCell;
  url: string;
  bounds?: SpriteBounds;
  lazy?: boolean;
  className?: string;
  onError?: () => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(!lazy);
  const [status, setStatus] = useState<SheetStatus>('loading');
  useEffect(() => {
    const el = ref.current;
    if (visible || !el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(es => {
      if (es.some(e => e.isIntersecting)) {
        setVisible(true);
        io.disconnect();
      }
    }, {
      rootMargin: '200px'
    });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);
  useEffect(() => {
    if (!visible) return;
    return watchSheet(url, s => {
      setStatus(s);
      if (s === 'error') onError?.();
    });
  }, [visible, url, onError]);

  // La lámina solo se pinta cuando está decodificada por completo: nunca se ve a medio cargar.
  const bg = visible && status === 'ok' ? `url("${url}")` : undefined;
  const loading = status !== 'ok' ? 'motion-safe:animate-pulse bg-white/[0.04]' : '';
  if (!bounds) {
    const last = ITEM_SHEET_GRID - 1;
    return <span ref={ref} aria-hidden="true" className={`block ${loading} ${className}`} style={{
      backgroundImage: bg,
      backgroundSize: `${ITEM_SHEET_GRID * 100}% ${ITEM_SHEET_GRID * 100}%`,
      backgroundPosition: `${cell.column / last * 100}% ${cell.row / last * 100}%`,
      backgroundRepeat: 'no-repeat'
    }} data-id="element-2043" data-mp="1vfm4gopz1ua4" />;
  }
  const {
    x0,
    y0,
    w,
    h,
    k
  } = spriteWindow(bounds);
  const sx = cell.column * BOUNDS_REFERENCE_CELL + x0;
  const sy = cell.row * BOUNDS_REFERENCE_CELL + y0;
  return <span ref={ref} aria-hidden="true" className={`relative block overflow-hidden ${loading} ${className}`} data-id="element-2044" data-mp="1uau7py1syp6j">
      <span className="absolute block overflow-hidden" style={{
      width: `${w * k * 100}%`,
      height: `${h * k * 100}%`,
      left: `${(1 - w * k) / 2 * 100}%`,
      top: `${(1 - h * k) / 2 * 100}%`,
      backgroundImage: bg,
      backgroundRepeat: 'no-repeat',
      // La ventana mide w x h px de referencia; la lámina entera mide SHEET_REF en esas mismas unidades.
      backgroundSize: `${SHEET_REF / w * 100}% ${SHEET_REF / h * 100}%`,
      backgroundPosition: `${sx / (SHEET_REF - w) * 100}% ${sy / (SHEET_REF - h) * 100}%`
    }} data-id="element-2045" data-mp="1z9xatcy82lbv" />
    </span>;
}